import { useEffect, useState } from "react";
import { apiJson } from "../../utils/api";
import { CoupleCodeBar, Disclaimer } from "./MarriageLayout";
import { ensureCoupleFromAccount, getCoupleCode, getUserId, setCoupleCode } from "../../utils/coupleSession";
import useSeo from "../../hooks/useSeo";

export default function MarriageMarriedLife() {
  const [code, setCode] = useState(getCoupleCode());
  const [side, setSide] = useState("male");
  const [scenes, setScenes] = useState([]);
  const [answers, setAnswers] = useState({});
  const [report, setReport] = useState(null);
  const [hint, setHint] = useState("");

  useSeo({
    title: "婚后生活模拟器",
    metaDescription: "家务、财务、买房、生育、双方父母五场景互动，生成婚姻协作指数（娱乐参考）。",
    path: "/marriage/married-life",
  });

  const applyCode = (value) => setCode(setCoupleCode(value));

  const ensureCode = async () => {
    if (code) return code;
    if (!getUserId()) {
      setHint("请先填写情侣纪念码，或登录后点「用我的账号创建」。否则选择不会被保存。");
      return "";
    }
    const data = await ensureCoupleFromAccount();
    applyCode(data.code);
    return data.code;
  };

  useEffect(() => {
    apiJson("/api/marriage/married-life/scenes").then(({ data }) => setScenes(data.items || []));
  }, []);

  useEffect(() => {
    if (code) return;
    if (!getUserId()) return;
    ensureCoupleFromAccount()
      .then((data) => applyCode(data.code))
      .catch(() => {});
  }, []);

  const save = async (sceneKey, answer) => {
    setAnswers((prev) => ({ ...prev, [sceneKey]: answer }));
    try {
      const live = await ensureCode();
      if (!live) return;
      const { res, data } = await apiJson(`/api/marriage/married-life/${live}/answers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scene_key: sceneKey, side, answer_text: answer }),
      });
      if (!res.ok) throw new Error(data?.detail || "保存失败");
      setHint("已保存这一题，请双方都选完再生成报告。");
    } catch (err) {
      setHint(err.message || "保存失败");
    }
  };

  const finish = async () => {
    try {
      const live = await ensureCode();
      if (!live) return;
      const { res, data } = await apiJson(`/api/marriage/married-life/${live}/report`, { method: "POST" });
      if (!res.ok) throw new Error(data?.detail || "请先创建纪念码并双方作答");
      setReport(data);
    } catch (err) {
      alert(err.message || "请先创建纪念码并双方作答");
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-extrabold">💑 婚后生活模拟器</h1>
      <p className="text-gray-600">现在你们已经结婚了，看看婚后会发生什么。五个场景都要选，选择会写进这对纪念码。</p>
      <Disclaimer />
      <CoupleCodeBar code={code} onChange={applyCode} />
      {hint ? <p className="text-sm text-amber-800">{hint}</p> : null}
      <div className="flex gap-2 text-sm">
        <button className={`px-3 py-1 rounded-full ${side === "male" ? "bg-rose-500 text-white" : "border"}`} onClick={() => setSide("male")}>
          我是男方
        </button>
        <button className={`px-3 py-1 rounded-full ${side === "female" ? "bg-pink-500 text-white" : "border"}`} onClick={() => setSide("female")}>
          我是女方
        </button>
      </div>
      <div className="space-y-4">
        {scenes.map((scene) => (
          <div key={scene.key} className="bg-white rounded-2xl p-5">
            <h2 className="font-bold">
              场景：{scene.title}
            </h2>
            <p className="text-gray-600 my-2">{scene.question}</p>
            <div className="flex flex-wrap gap-2">
              {scene.options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => save(scene.key, opt)}
                  className={`px-3 py-1.5 rounded-full text-sm border ${
                    answers[scene.key] === opt ? "bg-rose-500 text-white border-rose-500" : ""
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button onClick={finish} className="px-6 py-3 rounded-xl bg-rose-500 text-white">
        生成 AI 婚姻匹配报告
      </button>
      {report ? (
        <div className="bg-white rounded-2xl p-6 space-y-3">
          <h2 className="text-xl font-bold">AI婚姻匹配报告</h2>
          {report.overall != null ? (
            <p className="text-rose-600 font-extrabold text-2xl">综合指数 {report.overall}</p>
          ) : null}
          {Object.entries(report.scores || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 text-sm">
              <span className="w-24">{k}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-2">
                <div className="bg-rose-500 h-2 rounded-full" style={{ width: `${v}%` }} />
              </div>
              <span className="w-10">{v}{v < 70 ? " ⚠️" : ""}</span>
            </div>
          ))}
          <p className="font-bold">{report.summary}</p>
          <p>{report.advice}</p>
          {(report.details || []).length ? (
            <ul className="text-sm list-disc pl-5 text-gray-600">
              {report.details.map((d) => (
                <li key={d.scene}>
                  {d.scene}：男方「{d.male || "未答"}」／女方「{d.female || "未答"}」
                </li>
              ))}
            </ul>
          ) : null}
          <p className="text-xs text-gray-500">{report.disclaimer}</p>
        </div>
      ) : null}
    </div>
  );
}
